import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ExternalLink, Search, AlertTriangle, CheckCircle2, Scale, Package } from "lucide-react";
import licensesData from "@/lib/licenses.json";

type LicenseRisk = "safe" | "notice" | "warning";

interface LicenseInfo {
    risk: LicenseRisk;
    description: string;
}

const LICENSE_INFO: Record<string, LicenseInfo> = {
    MIT: { risk: "safe", description: "Permissive license - commercial use allowed" },
    "MIT-0": { risk: "safe", description: "MIT No Attribution - even more permissive" },
    ISC: { risk: "safe", description: "Permissive license similar to MIT" },
    "Apache-2.0": { risk: "safe", description: "Permissive license with patent protection" },
    "BSD-2-Clause": { risk: "safe", description: "Simplified BSD - permissive" },
    "BSD-3-Clause": { risk: "safe", description: "New BSD - permissive with attribution" },
    BSD: { risk: "safe", description: "BSD license - permissive" },
    "0BSD": { risk: "safe", description: "Zero-Clause BSD - public domain equivalent" },
    Unlicense: { risk: "safe", description: "Public domain dedication" },
    "CC0-1.0": { risk: "safe", description: "Creative Commons Zero - public domain" },
    "CC-BY-4.0": { risk: "safe", description: "Creative Commons Attribution" },
    "BlueOak-1.0.0": { risk: "safe", description: "Permissive license" },
    "Python-2.0": { risk: "safe", description: "Python Software Foundation License" },
    "(MIT OR Apache-2.0)": { risk: "safe", description: "Dual licensed - choose MIT or Apache" },
    "(Unlicense OR Apache-2.0)": { risk: "safe", description: "Dual licensed - choose Unlicense or Apache" },
    "(MIT OR CC0-1.0)": { risk: "safe", description: "Dual licensed - choose MIT or CC0" },
    "(MIT AND Zlib)": { risk: "safe", description: "Combined permissive licenses" },
    "MIT AND ISC": { risk: "safe", description: "Combined permissive licenses" },
    "(AFL-2.1 OR BSD-3-Clause)": { risk: "safe", description: "Dual licensed - choose AFL or BSD" },
    "MPL-2.0": { risk: "notice", description: "Mozilla Public License - file-level copyleft" },
    "LGPL-3.0-or-later": { risk: "notice", description: "Lesser GPL - dynamic linking allowed" },
    "(MIT OR GPL-3.0-or-later)": { risk: "safe", description: "Dual licensed - use MIT for commercial" },
    Unknown: { risk: "warning", description: "License not identified - verify manually" },
};

const getLicenseInfo = (license: string): LicenseInfo => {
    return LICENSE_INFO[license] || { risk: "warning", description: "License needs review" };
};

const getRiskBadgeVariant = (risk: LicenseRisk) => {
    switch (risk) {
        case "safe":
            return "outline" as const;
        case "notice":
            return "secondary" as const;
        case "warning":
            return "destructive" as const;
    }
};

const getRiskIcon = (risk: LicenseRisk) => {
    switch (risk) {
        case "safe":
            return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />;
        case "notice":
            return <Scale className="h-3.5 w-3.5 text-amber-500" />;
        case "warning":
            return <AlertTriangle className="h-3.5 w-3.5 text-red-500" />;
    }
};

const Licenses = () => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState("");
    const [filterLicense, setFilterLicense] = useState<string | null>(null);

    const filteredPackages = useMemo(() => {
        return licensesData.packages.filter((pkg) => {
            const matchesSearch =
                !searchQuery ||
                pkg.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                pkg.license.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesLicense = !filterLicense || pkg.license === filterLicense;
            return matchesSearch && matchesLicense;
        });
    }, [searchQuery, filterLicense]);

    const sortedLicenseSummary = useMemo(() => {
        return Object.entries(licensesData.licenseSummary)
            .sort(([, a], [, b]) => (b as number) - (a as number))
            .map(([license, count]) => ({
                license,
                count: count as number,
                info: getLicenseInfo(license),
            }));
    }, []);

    const stats = useMemo(() => {
        const total = licensesData.totalPackages;
        const safe = sortedLicenseSummary
            .filter((l) => l.info.risk === "safe")
            .reduce((acc, l) => acc + l.count, 0);
        const notice = sortedLicenseSummary
            .filter((l) => l.info.risk === "notice")
            .reduce((acc, l) => acc + l.count, 0);
        const warning = sortedLicenseSummary
            .filter((l) => l.info.risk === "warning")
            .reduce((acc, l) => acc + l.count, 0);
        return { total, safe, notice, warning };
    }, [sortedLicenseSummary]);

    return (
        <div className="container flex-1 flex-col flex-wrap items-center px-2 pb-8 md:px-4 md:pt-2 lg:px-4">
            <div className="px-1 pt-3 space-y-6">
                {/* Header */}
                <div className="space-y-2">
                    <h2 className="text-2xl font-bold">{t("licenses.title")}</h2>
                    <p className="text-base text-muted-foreground">
                        {t("licenses.description")}
                    </p>
                </div>

                {/* Stats Cards */}
                <div className="grid gap-4 md:grid-cols-4">
                    <Card className="border-border/50">
                        <CardHeader className="pb-2">
                            <CardDescription>{t("licenses.stats.totalPackages")}</CardDescription>
                            <CardTitle className="text-3xl font-bold">{stats.total}</CardTitle>
                            <p className="text-xs text-muted-foreground mt-1">{t("licenses.stats.includingTransitive")}</p>
                        </CardHeader>
                    </Card>
                    <Card className="border-emerald-500/20 bg-emerald-500/5">
                        <CardHeader className="pb-2">
                            <CardDescription className="flex items-center gap-1.5">
                                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                                {t("licenses.stats.permissive")}
                            </CardDescription>
                            <CardTitle className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                                {stats.safe}
                            </CardTitle>
                        </CardHeader>
                    </Card>
                    <Card className="border-amber-500/20 bg-amber-500/5">
                        <CardHeader className="pb-2">
                            <CardDescription className="flex items-center gap-1.5">
                                <Scale className="h-3.5 w-3.5 text-amber-500" />
                                {t("licenses.stats.copyleft")}
                            </CardDescription>
                            <CardTitle className="text-3xl font-bold text-amber-600 dark:text-amber-400">
                                {stats.notice}
                            </CardTitle>
                        </CardHeader>
                    </Card>
                    <Card className="border-red-500/20 bg-red-500/5">
                        <CardHeader className="pb-2">
                            <CardDescription className="flex items-center gap-1.5">
                                <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                                {t("licenses.stats.unknown")}
                            </CardDescription>
                            <CardTitle className="text-3xl font-bold text-red-600 dark:text-red-400">
                                {stats.warning}
                            </CardTitle>
                        </CardHeader>
                    </Card>
                </div>

                {/* License Summary */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Scale className="h-5 w-5" />
                            {t("licenses.licenseSummary")}
                        </CardTitle>
                        <CardDescription>{t("licenses.licenseSummaryDescription")}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap gap-2">
                            {sortedLicenseSummary.map(({ license, count, info }) => (
                                <Badge
                                    key={license}
                                    variant={getRiskBadgeVariant(info.risk)}
                                    className="cursor-pointer hover:opacity-80 transition-opacity text-xs gap-1.5 py-1 px-2"
                                    onClick={() => setFilterLicense(filterLicense === license ? null : license)}
                                    title={info.description}
                                >
                                    {getRiskIcon(info.risk)}
                                    <span className="font-medium">{license}</span>
                                    <span className="opacity-60">({count})</span>
                                </Badge>
                            ))}
                        </div>
                        {filterLicense && (
                            <div className="mt-3 text-sm text-muted-foreground">
                                {t("licenses.filteringBy", { license: filterLicense })}
                                <button
                                    onClick={() => setFilterLicense(null)}
                                    className="ml-2 text-primary hover:underline"
                                >
                                    {t("licenses.clearFilter")}
                                </button>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Notice for Copyleft Licenses */}
                {stats.notice > 0 && (
                    <Card className="border-amber-500/30 bg-amber-500/5">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2 text-amber-700 dark:text-amber-400">
                                <Scale className="h-5 w-5" />
                                {t("licenses.copyleftNotice.title")}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-amber-800/80 dark:text-amber-200/80 space-y-2">
                            <p>{t("licenses.copyleftNotice.description")}</p>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>
                                    <strong>LGPL-3.0:</strong> {t("licenses.copyleftNotice.lgpl")}
                                </li>
                                <li>
                                    <strong>MPL-2.0:</strong> {t("licenses.copyleftNotice.mpl")}
                                </li>
                            </ul>
                        </CardContent>
                    </Card>
                )}

                {/* Package List */}
                <Card>
                    <CardHeader>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Package className="h-5 w-5" />
                                    {t("licenses.packageList")}
                                </CardTitle>
                                <CardDescription>
                                    {t("licenses.showingPackages", { count: filteredPackages.length, total: stats.total })}
                                </CardDescription>
                            </div>
                            <div className="relative w-full sm:w-72">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder={t("licenses.searchPlaceholder")}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-9"
                                />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <ScrollArea className="h-[500px] rounded-md border">
                            <div className="divide-y divide-border">
                                {filteredPackages.map((pkg, index) => {
                                    const info = getLicenseInfo(pkg.license);
                                    return (
                                        <div
                                            key={`${pkg.name}-${index}`}
                                            className="flex items-start justify-between gap-4 p-3 hover:bg-muted/50 transition-colors"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-mono text-sm font-medium truncate">
                                                        {pkg.name}
                                                    </span>
                                                    <span className="text-xs text-muted-foreground">
                                                        v{pkg.version}
                                                    </span>
                                                </div>
                                                {pkg.description && (
                                                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                                                        {pkg.description}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <Badge
                                                    variant={getRiskBadgeVariant(info.risk)}
                                                    className="text-xs gap-1"
                                                >
                                                    {getRiskIcon(info.risk)}
                                                    {pkg.license}
                                                </Badge>
                                                {pkg.homepage && (
                                                    <a
                                                        href={pkg.homepage}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-muted-foreground hover:text-foreground transition-colors"
                                                    >
                                                        <ExternalLink className="h-4 w-4" />
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                {filteredPackages.length === 0 && (
                                    <div className="p-8 text-center text-muted-foreground">
                                        {t("licenses.noPackagesFound")}
                                    </div>
                                )}
                            </div>
                        </ScrollArea>
                    </CardContent>
                </Card>

                {/* Footer */}
                <div className="text-xs text-muted-foreground text-center">
                    {t("licenses.generatedAt", { date: new Date(licensesData.generatedAt).toLocaleDateString() })}
                </div>
            </div>
        </div>
    );
};

export default Licenses;

